import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listOrganizations,
  updateOrganization,
  getOrgSubscription,
  updateOrgPlan,
  addOrgCredits,
  type AdminOrg,
  type SubscriptionWithUsage,
} from '../api/admin';

const PLANS = ['starter', 'pro', 'business', 'enterprise'] as const;

const PLAN_COLORS: Record<string, { bg: string; text: string }> = {
  starter: { bg: 'bg-emerald-50', text: 'text-emerald-700' },
  pro: { bg: 'bg-blue-50', text: 'text-blue-700' },
  business: { bg: 'bg-amber-50', text: 'text-amber-700' },
  enterprise: { bg: 'bg-purple-50', text: 'text-purple-700' },
};

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

function SubscriptionPanel({ orgId, onClose }: { orgId: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [bonusTokens, setBonusTokens] = useState(0);
  const [bonusRequests, setBonusRequests] = useState(0);
  const [creditReason, setCreditReason] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const subQuery = useQuery<SubscriptionWithUsage>({
    queryKey: ['admin', 'subscription', orgId],
    queryFn: () => getOrgSubscription(orgId),
  });

  const planMutation = useMutation({
    mutationFn: (plan: string) => updateOrgPlan(orgId, plan),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'subscription', orgId] });
      setSuccessMsg('Plan updated');
      setTimeout(() => setSuccessMsg(''), 2500);
    },
  });

  const creditsMutation = useMutation({
    mutationFn: () =>
      addOrgCredits(orgId, {
        bonus_tokens: bonusTokens,
        bonus_requests: bonusRequests,
        reason: creditReason || undefined,
      }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'subscription', orgId] });
      setSuccessMsg(res.message);
      setBonusTokens(0);
      setBonusRequests(0);
      setCreditReason('');
      setTimeout(() => setSuccessMsg(''), 3000);
    },
  });

  if (subQuery.isLoading) {
    return (
      <tr>
        <td colSpan={6} className="px-6 py-8 text-center text-slate-400">
          Loading subscription...
        </td>
      </tr>
    );
  }

  if (subQuery.error || !subQuery.data) {
    return (
      <tr>
        <td colSpan={6} className="px-6 py-8 text-center text-red-500">
          Failed to load subscription data.
        </td>
      </tr>
    );
  }

  const { subscription: sub, usage, effective_limits: limits } = subQuery.data;
  const planStyle = PLAN_COLORS[sub.plan] || PLAN_COLORS.starter;

  return (
    <tr>
      <td colSpan={6} className="px-6 py-6 bg-slate-50 border-t border-slate-100">
        <div className="space-y-5">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-sm font-semibold text-slate-900">Subscription Management</span>
              <span className={`px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${planStyle.bg} ${planStyle.text}`}>
                {sub.plan}
              </span>
            </div>
            <button
              onClick={onClose}
              className="text-xs text-slate-500 hover:text-slate-900 px-3 py-1.5 rounded-lg hover:bg-slate-200 transition-colors"
            >
              Close
            </button>
          </div>

          {successMsg && (
            <div className="px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg text-xs font-semibold text-emerald-700">
              {successMsg}
            </div>
          )}

          {/* Usage stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg border border-slate-200 p-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Tokens used</div>
              <div className="text-lg font-bold text-slate-900">{formatNumber(usage.tokens_used)}</div>
              <div className="text-[10px] text-slate-500">/ {formatNumber(limits.total_token_limit)} ({limits.token_usage_percent}%)</div>
            </div>
            <div className="bg-white rounded-lg border border-slate-200 p-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Requests used</div>
              <div className="text-lg font-bold text-slate-900">{formatNumber(usage.requests_used)}</div>
              <div className="text-[10px] text-slate-500">/ {formatNumber(limits.total_request_limit)} ({limits.request_usage_percent}%)</div>
            </div>
            <div className="bg-white rounded-lg border border-slate-200 p-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Bonus tokens</div>
              <div className="text-lg font-bold text-slate-900">{formatNumber(sub.bonus_tokens)}</div>
            </div>
            <div className="bg-white rounded-lg border border-slate-200 p-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Days remaining</div>
              <div className="text-lg font-bold text-slate-900">{usage.days_remaining}</div>
            </div>
          </div>

          {/* Change plan */}
          <div className="bg-white rounded-lg border border-slate-200 p-4">
            <div className="text-xs font-semibold text-slate-700 mb-3">Change Plan</div>
            <div className="flex items-center gap-2">
              {PLANS.map((plan) => (
                <button
                  key={plan}
                  onClick={() => planMutation.mutate(plan)}
                  disabled={planMutation.isPending || sub.plan === plan}
                  className={`px-4 py-2 text-xs font-semibold rounded-lg border transition-colors disabled:opacity-50 ${
                    sub.plan === plan
                      ? 'bg-slate-900 text-white border-slate-900'
                      : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  {plan.charAt(0).toUpperCase() + plan.slice(1)}
                </button>
              ))}
              {planMutation.isError && (
                <span className="text-xs text-red-600 ml-2">Failed to update plan</span>
              )}
            </div>
          </div>

          {/* Add credits */}
          <div className="bg-white rounded-lg border border-slate-200 p-4">
            <div className="text-xs font-semibold text-slate-700 mb-3">Add Credits</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
              <label className="block">
                <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1">Bonus tokens</div>
                <input
                  type="number"
                  min={0}
                  value={bonusTokens}
                  onChange={(e) => setBonusTokens(Math.max(0, parseInt(e.target.value) || 0))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                />
              </label>
              <label className="block">
                <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1">Bonus requests</div>
                <input
                  type="number"
                  min={0}
                  value={bonusRequests}
                  onChange={(e) => setBonusRequests(Math.max(0, parseInt(e.target.value) || 0))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                />
              </label>
              <label className="block">
                <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1">Reason (optional)</div>
                <input
                  type="text"
                  value={creditReason}
                  onChange={(e) => setCreditReason(e.target.value)}
                  placeholder="e.g. support request"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                />
              </label>
            </div>
            <button
              onClick={() => creditsMutation.mutate()}
              disabled={creditsMutation.isPending || (bonusTokens === 0 && bonusRequests === 0)}
              className="px-4 py-2 text-xs font-semibold rounded-lg bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50 transition-colors"
            >
              {creditsMutation.isPending ? 'Adding...' : 'Add Credits'}
            </button>
            {creditsMutation.isError && (
              <span className="text-xs text-red-600 ml-3">Failed to add credits</span>
            )}
          </div>
        </div>
      </td>
    </tr>
  );
}

export function OrganizationsPage() {
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState('');
  const [expandedOrg, setExpandedOrg] = useState<string | null>(null);

  const orgsQuery = useQuery({
    queryKey: ['admin', 'organizations'],
    queryFn: listOrganizations,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: { is_blocked?: boolean; restart_trial?: boolean } }) =>
      updateOrganization(id, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'organizations'] }),
  });

  const orgs = (orgsQuery.data || []).filter(
    (o: AdminOrg) =>
      !filter ||
      o.name.toLowerCase().includes(filter.toLowerCase()) ||
      (o.email && o.email.toLowerCase().includes(filter.toLowerCase())),
  );

  const blockedCount = orgs.filter((o: AdminOrg) => o.is_blocked).length;

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900">Organizations</h1>
        <p className="text-slate-600 mt-2">
          Manage organizations, subscriptions, and platform access
        </p>
      </div>

      {blockedCount > 0 && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <svg className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
          </svg>
          <div>
            <div className="font-semibold text-red-900">
              {blockedCount} organization{blockedCount !== 1 ? 's' : ''} blocked
            </div>
            <div className="text-sm text-red-700 mt-1">
              Blocked organizations cannot access platform services
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center gap-4 mb-6">
        <div className="flex-1">
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search by name or email..."
            className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        </div>
        <div className="text-sm text-slate-600">
          {orgs.length} organization{orgs.length !== 1 ? 's' : ''}
        </div>
      </div>

      {orgsQuery.isLoading && (
        <div className="text-center py-12 text-slate-400">Loading organizations...</div>
      )}

      {!orgsQuery.isLoading && orgs.length === 0 && (
        <div className="text-center py-12 text-slate-400">No organizations found</div>
      )}

      {!orgsQuery.isLoading && orgs.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                <th className="px-6 py-4">Organization</th>
                <th className="px-6 py-4 text-center">Sites</th>
                <th className="px-6 py-4 text-center">Users</th>
                <th className="px-6 py-4 text-center">Status</th>
                <th className="px-6 py-4">Created</th>
                <th className="px-6 py-4"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {orgs.map((org: AdminOrg) => (
                <>
                  <tr key={org.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="font-medium text-slate-900">{org.name}</div>
                      <div className="text-slate-500 text-xs">{org.email || '—'}</div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-blue-50 text-blue-700 font-semibold text-sm">
                        {org.site_count}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-purple-50 text-purple-700 font-semibold text-sm">
                        {org.user_count}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span
                        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${
                          org.is_blocked
                            ? 'bg-red-50 text-red-700'
                            : 'bg-green-50 text-green-700'
                        }`}
                      >
                        <span
                          className={`w-1.5 h-1.5 rounded-full ${
                            org.is_blocked ? 'bg-red-500' : 'bg-green-500'
                          }`}
                        />
                        {org.is_blocked ? 'Blocked' : 'Active'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-slate-500 text-xs">
                      {new Date(org.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() =>
                            setExpandedOrg(expandedOrg === org.id ? null : org.id)
                          }
                          className={`px-4 py-2 text-xs font-medium rounded-lg border transition-colors ${
                            expandedOrg === org.id
                              ? 'bg-purple-600 text-white border-purple-600'
                              : 'text-purple-700 border-purple-200 hover:bg-purple-50'
                          }`}
                        >
                          {expandedOrg === org.id ? 'Hide' : 'Subscription'}
                        </button>
                        <button
                          onClick={() =>
                            updateMutation.mutate({
                              id: org.id,
                              payload: { is_blocked: !org.is_blocked },
                            })
                          }
                          disabled={updateMutation.isPending}
                          className={`px-4 py-2 text-xs font-medium rounded-lg transition-colors disabled:opacity-50 ${
                            org.is_blocked
                              ? 'text-green-700 border border-green-200 hover:bg-green-50 bg-green-50'
                              : 'text-red-700 border border-red-200 hover:bg-red-50'
                          }`}
                        >
                          {org.is_blocked ? 'Unblock' : 'Block'}
                        </button>
                        <button
                          onClick={() => {
                            if (window.confirm(`Restart the free trial for ${org.name} from today? Only affects orgs on the Free plan.`)) {
                              updateMutation.mutate({ id: org.id, payload: { restart_trial: true } });
                            }
                          }}
                          disabled={updateMutation.isPending}
                          title="Start the Free-plan trial again from today"
                          className="px-4 py-2 text-xs font-medium rounded-lg border text-amber-700 border-amber-200 hover:bg-amber-50 transition-colors disabled:opacity-50"
                        >
                          Restart trial
                        </button>
                      </div>
                    </td>
                  </tr>
                  {expandedOrg === org.id && (
                    <SubscriptionPanel
                      key={`sub-${org.id}`}
                      orgId={org.id}
                      onClose={() => setExpandedOrg(null)}
                    />
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
